/*
  cp-bb — BoneBuddy PDP controller.

  Presentation only. The colour radios drive the variant, and the name, phone
  and font controls are themselves the line-item-property inputs, so the form
  still adds the right thing to the cart with this file blocked or broken.
  What is lost without it is the live engraving, the price swap and the
  disabled state on the button.

  The engraving preview is drawn TWICE — once on the gallery stage, once
  inside step 2 — and both copies carry the same data-cp-bb hooks. Everything
  here uses qa() + forEach, never a single q(), so the two cannot disagree.
  A third copy would need no change to this file.

  Read per-colour geometry off the radio's data-* attributes rather than a
  JSON blob, so a colour added in the theme editor works with no build step.
*/
(function () {
  'use strict';

  var qa = function (root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  };

  function sizeFor(len, cap, ref, min) {
    if (!len) len = 1;
    var v = Math.min(cap, (cap * ref) / len);
    return Math.max(min, v).toFixed(2) + 'cqw';
  }

  function init(root) {
    if (root.dataset.cpBbReady === '1') return;
    root.dataset.cpBbReady = '1';

    var colourInputs = qa(root, '[data-cp-bb-colour]');
    var fontInputs = qa(root, '[data-cp-bb-font]');
    var nameInput = root.querySelector('[data-cp-bb-name]');
    var phoneInput = root.querySelector('[data-cp-bb-phone]');
    var variantField = root.querySelector('[data-cp-bb-variant]');
    var cta = root.querySelector('[data-cp-bb-cta]');
    var sticky = root.querySelector('[data-cp-bb-sticky]');
    var stickyBtn = root.querySelector('[data-cp-bb-stickybtn]');
    var form = root.querySelector('form');

    var nameFallback = root.dataset.cpBbNameFallback || 'Their name';
    var phoneFallback = root.dataset.cpBbPhoneFallback || '04XX XXX XXX';
    var nameMin = parseFloat(root.dataset.cpBbNameMin || '5.2');
    var phoneMin = parseFloat(root.dataset.cpBbPhoneMin || '3.6');

    function selected(list) {
      for (var i = 0; i < list.length; i++) if (list[i].checked) return list[i];
      return list[0] || null;
    }

    /* ------------------------------------------------------------ gallery */

    var panes = qa(root, '[data-cp-bb-pane]');
    var thumbs = qa(root, '[data-cp-bb-thumb]');
    var liveThumb = root.querySelector('[data-cp-bb-thumb="live"]');
    var liveRevealed = false;

    function show(key) {
      panes.forEach(function (p) {
        var on = p.dataset.cpBbPane === key;
        p.hidden = !on;
        qa(p, 'video').forEach(function (v) {
          if (on) { var r = v.play(); if (r && r.catch) r.catch(function () {}); }
          else v.pause();
        });
      });
      thumbs.forEach(function (t) {
        var on = t.dataset.cpBbThumb === key;
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) revealThumb(t);
      });
    }

    /*
      Scroll the strip — never the page — so the active thumbnail is in view.
      scrollIntoView() would also scroll the window vertically, which yanks
      the shopper away from the colour picker they just clicked.
    */
    function revealThumb(t) {
      var strip = t.parentNode;
      if (!strip || t.hidden) return;
      var left = t.offsetLeft;
      var right = left + t.offsetWidth;
      if (left < strip.scrollLeft || right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: Math.max(0, left - 8), behavior: 'smooth' });
      }
    }

    /*
      A colour change moves the stage to that colour. Once a name is typed the
      live engraving IS that colour, so it wins; before that, the colour's own
      product photo does. The pane key comes from the colour block's gallery
      setting, falling back to the variant's image — see cp-bb-hero.
    */
    function followColour() {
      var colour = selected(colourInputs);
      if (!colour) return;
      var hasName = nameInput && nameInput.value.trim().length > 0;
      if (hasName && liveThumb) { show('live'); return; }
      var key = colour.dataset.cpBbMedia;
      if (key && root.querySelector('[data-cp-bb-pane="' + key + '"]')) show(key);
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { show(t.dataset.cpBbThumb); });
    });

    function currentKey() {
      for (var i = 0; i < panes.length; i++) if (!panes[i].hidden) return panes[i].dataset.cpBbPane;
      return null;
    }

    /*
      An engraving preview of an empty name is just a picture of a blank tag,
      so the live pane stays hidden until the shopper types. It takes the
      stage once — the latch stops it yanking the stage back on every
      keystroke — and on clear it hands the stage back to the first pane.
    */
    function syncLiveThumb(hasName) {
      if (!liveThumb) return;
      if (hasName) {
        liveThumb.hidden = false;
        if (!liveRevealed) { liveRevealed = true; show('live'); }
      } else {
        liveThumb.hidden = true;
        liveRevealed = false;
        if (currentKey() === 'live') {
          var first = panes.filter(function (p) { return p.dataset.cpBbPane !== 'live'; })[0];
          if (first) show(first.dataset.cpBbPane);
        }
      }
    }

    /* ------------------------------------------------------------ preview */

    /*
      The character-count formula guesses a size; it cannot know that Archivo
      Black is half as wide again as Baloo, or that Dancing Script's swashes
      hang past the glyph box. So after the guess, measure one visible copy
      and shrink name and number until they sit inside the engraving box —
      width first, then the stacked height of both lines.

      Sizes stay in cqw. Box and text both scale with the stage, so the ratio
      measured here is the same at every screen width and on every copy: the
      280px step-2 preview and the full-width gallery stage get one answer.
    */
    var FIT = 0.9;

    function fitEngraving() {
      var boxes = qa(root, '[data-cp-bb-box]');
      var box = null;
      for (var i = 0; i < boxes.length; i++) {
        if (boxes[i].clientWidth > 0 && boxes[i].clientHeight > 0) { box = boxes[i]; break; }
      }
      if (!box) return;

      var nameEl = box.querySelector('[data-cp-bb-engname]');
      var phoneEl = box.querySelector('[data-cp-bb-engphone]');
      if (!nameEl || !phoneEl) return;

      var n = parseFloat(nameEl.style.fontSize) || 0;
      var p = parseFloat(phoneEl.style.fontSize) || 0;
      if (!n || !p) return;

      var availW = box.clientWidth * FIT;
      if (nameEl.scrollWidth > availW) n = n * availW / nameEl.scrollWidth;
      if (phoneEl.scrollWidth > availW) p = p * availW / phoneEl.scrollWidth;

      apply(n, p);

      var gap = parseFloat(getComputedStyle(box).rowGap) || 0;
      var stack = nameEl.offsetHeight + phoneEl.offsetHeight + gap;
      var availH = box.clientHeight * 0.96;
      if (stack > availH) {
        var k = availH / stack;
        apply(n * k, p * k);
      }
    }

    function apply(n, p) {
      qa(root, '[data-cp-bb-engname]').forEach(function (el) { el.style.fontSize = n.toFixed(2) + 'cqw'; });
      qa(root, '[data-cp-bb-engphone]').forEach(function (el) { el.style.fontSize = p.toFixed(2) + 'cqw'; });
    }

    function render() {
      var colour = selected(colourInputs);
      var font = selected(fontInputs);

      var rawName = nameInput ? nameInput.value.trim() : '';
      var rawPhone = phoneInput ? phoneInput.value.trim() : '';
      var nameText = rawName || nameFallback;
      var phoneText = rawPhone || phoneFallback;

      var scale = font ? parseFloat(font.dataset.cpBbScale || '1') : 1;
      if (!isFinite(scale) || scale <= 0) scale = 1;

      var d = colour ? colour.dataset : {};
      var nameCap = parseFloat(d.cpBbNameCap || '12.4');
      var nameRef = parseFloat(d.cpBbNameRef || '5.2');
      var phoneCap = parseFloat(d.cpBbPhoneCap || '6.2');
      var phoneRef = parseFloat(d.cpBbPhoneRef || '12.26');

      var nLen = Math.max(nameText.length, 3);
      var pLen = Math.max(phoneText.length, 6);

      qa(root, '[data-cp-bb-plate]').forEach(function (img) {
        if (d.cpBbImage && img.getAttribute('src') !== d.cpBbImage) img.setAttribute('src', d.cpBbImage);
        if (d.cpBbAlt) img.setAttribute('alt', d.cpBbAlt);
      });

      qa(root, '[data-cp-bb-box]').forEach(function (box) {
        box.style.left = d.cpBbL || '27.3%';
        box.style.top = d.cpBbT || '42%';
        box.style.width = d.cpBbW || '45%';
        box.style.height = d.cpBbH || '20.4%';
      });

      qa(root, '[data-cp-bb-engname]').forEach(function (el) {
        el.textContent = nameText;
        el.style.opacity = rawName ? '1' : '0.32';
        el.style.color = d.cpBbInk || '#17191c';
        el.style.textShadow = d.cpBbShadow || 'none';
        el.style.fontSize = sizeFor(nLen, nameCap * scale, nameRef, nameMin);
        if (font) {
          el.style.fontFamily = font.dataset.cpBbFamily || '';
          el.style.fontWeight = font.dataset.cpBbWeight || '700';
          el.style.fontStyle = font.dataset.cpBbStyle || 'normal';
          el.style.letterSpacing = font.dataset.cpBbSpacing || 'normal';
        }
      });

      qa(root, '[data-cp-bb-engphone]').forEach(function (el) {
        el.textContent = phoneText;
        el.style.opacity = rawPhone ? '1' : '0.28';
        el.style.color = d.cpBbInk || '#17191c';
        el.style.textShadow = d.cpBbShadow || 'none';
        el.style.fontSize = sizeFor(pLen, phoneCap, phoneRef, phoneMin);
      });

      fitEngraving();

      /* -------------------------------------------------------- labels */

      qa(root, '[data-cp-bb-colourname]').forEach(function (el) {
        el.textContent = colour ? (colour.dataset.cpBbLabel || '') : '';
      });
      qa(root, '[data-cp-bb-fontname]').forEach(function (el) {
        el.textContent = font ? (font.dataset.cpBbLabel || '') : '';
      });

      /* --------------------------------------------------------- money */

      if (colour) {
        if (variantField && colour.dataset.cpBbVariant) variantField.value = colour.dataset.cpBbVariant;

        qa(root, '[data-cp-bb-price]').forEach(function (el) {
          el.textContent = colour.dataset.cpBbPrice || el.textContent;
        });
        qa(root, '[data-cp-bb-compare]').forEach(function (el) {
          var v = colour.dataset.cpBbCompare || '';
          el.textContent = v;
          el.hidden = !v;
        });
        qa(root, '[data-cp-bb-save]').forEach(function (el) {
          var v = colour.dataset.cpBbSave || '';
          el.textContent = v;
          el.hidden = !v;
        });
      }

      /* ----------------------------------------------------------- cta */

      var available = colour ? colour.dataset.cpBbAvailable === 'true' : false;
      var ready = rawName.length > 0 && available;

      if (cta) {
        var label;
        if (!available) label = cta.dataset.cpBbSoldout || 'Sold out';
        else if (!rawName) label = cta.dataset.cpBbBlocked || 'Add a name to continue';
        else label = (cta.dataset.cpBbReady || 'Add to cart').replace('{amount}', (colour && colour.dataset.cpBbPrice) || '');
        // Write the label span only — cta.textContent would wipe the icon.
        var labelEl = cta.querySelector('[data-cp-bb-ctalabel]');
        if (labelEl) labelEl.textContent = label; else cta.textContent = label;
        cta.classList.toggle('cp-bb-cta--soldout', !available);
        /* Only sold out is truly disabled. Waiting for a name stays a live
           button (aria-disabled), because a disabled button swallows the tap
           and the shopper gets no answer — the click guard below sends them
           to the name field instead. */
        cta.disabled = !available;
        cta.setAttribute('aria-disabled', ready ? 'false' : 'true');
      }

      if (stickyBtn) {
        stickyBtn.dataset.state = !available ? 'soldout' : ready ? 'ready' : 'blocked';
        stickyBtn.textContent = !available
          ? (stickyBtn.dataset.cpBbSoldout || 'Sold out')
          : ready ? (stickyBtn.dataset.cpBbReady || 'Add to cart') : (stickyBtn.dataset.cpBbBlocked || 'Personalise');
      }
      if (colour) {
        qa(root, '[data-cp-bb-stickyprice]').forEach(function (el) {
          el.textContent = colour.dataset.cpBbPrice || el.textContent;
        });
        qa(root, '[data-cp-bb-stickyimg]').forEach(function (img) {
          var src = colour.dataset.cpBbSwatch;
          if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
        });
      }

      qa(root, '[data-cp-bb-hint]').forEach(function (el) {
        el.textContent = ready ? (el.dataset.cpBbOk || '') : (el.dataset.cpBbEmpty || '');
      });

      /* -------------------------------------------------------- counts */

      if (nameInput) {
        qa(root, '[data-cp-bb-namecount]').forEach(function (el) {
          el.textContent = nameInput.value.length + '/' + (nameInput.maxLength > 0 ? nameInput.maxLength : '');
        });
        qa(root, '[data-cp-bb-namehint]').forEach(function (el) {
          el.textContent = rawName ? (el.dataset.cpBbFull || '') : (el.dataset.cpBbEmpty || '');
        });
      }
      if (phoneInput) {
        qa(root, '[data-cp-bb-phonecount]').forEach(function (el) {
          el.textContent = phoneInput.value.length + '/' + (phoneInput.maxLength > 0 ? phoneInput.maxLength : '');
        });
        qa(root, '[data-cp-bb-phonehint]').forEach(function (el) {
          el.textContent = rawPhone.length >= 6 ? (el.dataset.cpBbFull || '') : (el.dataset.cpBbEmpty || '');
        });
      }

      syncLiveThumb(rawName.length > 0);
    }

    /* -------------------------------------------------------------- wire */

    colourInputs.forEach(function (i) {
      i.addEventListener('change', function () { render(); followColour(); });
    });
    fontInputs.forEach(function (i) { i.addEventListener('change', render); });

    /*
      The engraving fonts come from Google and land after first paint. Until
      they do, the fit is measured against the fallback face, so measure
      again once each one arrives — otherwise Block and Script overflow on a
      cold load exactly as they did before the fit existed.
    */
    if (document.fonts) {
      if (document.fonts.ready) document.fonts.ready.then(render);
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', render);
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(render, 120);
    });

    if (nameInput) {
      nameInput.addEventListener('input', function () {
        var pattern = nameInput.dataset.cpBbStrip;
        if (pattern) {
          /* Accented letters always pass, the same set the collars take. */
          var cleaned = nameInput.value.normalize('NFC').replace(new RegExp(pattern.replace('[^', '[^À-ÖØ-öø-ſ'), 'g'), '');
          if (cleaned !== nameInput.value) nameInput.value = cleaned;
        }
        render();
      });
    }
    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        var pattern = phoneInput.dataset.cpBbStrip;
        if (pattern) {
          var cleaned = phoneInput.value.replace(new RegExp(pattern, 'g'), '');
          if (cleaned !== phoneInput.value) phoneInput.value = cleaned;
        }
        render();
      });
    }

    /* ------------------------------------------------- blocked button */

    /*
      Send the shopper to the name field and say why, rather than doing
      nothing. On click, not submit: the theme's own cart script may listen
      for submit, and preventing the click stops the submission before any
      of it runs — Enter in a field presses this same button.
    */
    function askForName() {
      if (!nameInput) return;
      nameInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
      nameInput.focus({ preventScroll: true });
      nameInput.classList.remove('is-missing');
      void nameInput.offsetWidth; // restart the nudge if it is tapped twice
      nameInput.classList.add('is-missing');
    }

    if (cta) {
      cta.addEventListener('click', function (e) {
        if (cta.getAttribute('aria-disabled') !== 'true') return;
        e.preventDefault();
        e.stopImmediatePropagation();
        askForName();
      });
    }
    if (nameInput) {
      nameInput.addEventListener('input', function () { nameInput.classList.remove('is-missing'); });
      /* "Next" on the phone keyboard moves to the number, not a submit. */
      nameInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && phoneInput) { e.preventDefault(); phoneInput.focus(); }
      });
    }

    /* ----------------------------------------------------- stage swipe */

    /*
      On a phone the photo is the natural thing to swipe. A sideways swipe
      steps to the neighbouring visible thumbnail, exactly as a tap on it
      would; vertical drags are left to the page.
    */
    var stage = root.querySelector('.cp-bb-stage');
    if (stage) {
      var sx = 0, sy = 0, tracking = false;
      stage.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        tracking = true;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var t = e.changedTouches[0];
        var dx = t.clientX - sx;
        var dy = t.clientY - sy;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        var visible = thumbs.filter(function (b) { return !b.hidden; });
        var at = -1;
        for (var i = 0; i < visible.length; i++) if (visible[i].getAttribute('aria-pressed') === 'true') at = i;
        var next = visible[at + (dx < 0 ? 1 : -1)];
        if (next) show(next.dataset.cpBbThumb);
      }, { passive: true });
    }

    /* ------------------------------------------------------ sticky bar */

    if (sticky && form && 'IntersectionObserver' in window) {
      var formVisible = true;
      var setSticky = function () {
        var on = !formVisible && form.getBoundingClientRect().bottom < 0;
        sticky.classList.toggle("is-shown", on);
        document.documentElement.classList.toggle("cp-bb-sticky-on", on);
      };
      new IntersectionObserver(function (entries) {
        formVisible = entries[0].isIntersecting;
        setSticky();
      }).observe(form);
      window.addEventListener('scroll', function () {
        if (!formVisible) setSticky();
      }, { passive: true });

      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          var state = stickyBtn.dataset.state;
          if (state === 'soldout') return;
          /* The real submit button, so the theme's cart handling runs
             exactly as for a direct tap. */
          if (state === 'ready' && cta) { cta.click(); return; }
          askForName();
        });
      }
    }

    render();
  }

  function boot() { qa(document, '[data-cp-bb-root]').forEach(init); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* The theme editor re-renders a section in place; re-init the new copy. */
  document.addEventListener('shopify:section:load', function (e) {
    qa(e.target, '[data-cp-bb-root]').forEach(init);
  });
})();
